<template>
  <Dialog v-model:open="isOpen">
    <DialogContent
      class="max-h-[90vh] w-[96vw] max-w-3xl overflow-y-auto rounded-[1.35rem] border border-[#d8e4f0] bg-white p-0 shadow-[0_24px_60px_rgba(15,23,42,0.2)]"
    >
      <div class="border-b border-[#e3eaf4] px-6 py-5">
        <DialogHeader class="gap-y-2 text-left">
          <DialogTitle class="text-[1.35rem] font-extrabold text-[#0f172a]">
            Edit Generated Report
          </DialogTitle>
          <DialogDescription class="text-[#64748b]">
            Update the report text fields and save to regenerate the preview. Only enabled sections are listed.
          </DialogDescription>
        </DialogHeader>
      </div>

      <form
        class="space-y-5 px-6 py-5"
        @submit.prevent="submit"
      >
        <div class="grid gap-4 md:grid-cols-2">
          <!-- Main metadata fields -->
          <div class="space-y-2 md:col-span-2">
            <label class="field-label">Report title</label>
            <input
              v-model="form.title"
              class="field-input"
              type="text"
            >
          </div>

          <div class="space-y-2 md:col-span-2">
            <label class="field-label">Prepared by</label>
            <input
              v-model="form.prepared_by"
              class="field-input"
              type="text"
            >
          </div>

          <div class="space-y-2 md:col-span-2">
            <label class="field-label">Executive snapshot note</label>
            <textarea
              v-model="form.executive_snapshot_note"
              class="field-textarea"
              rows="4"
            />
          </div>

          <!-- Dynamic Optional Sections -->
          <!-- Social Media Performance -->
          <template v-if="sectionEnabled('socialMediaPerformance')">
            <div class="space-y-2 md:col-span-2 border-t border-[#f0f4fa] pt-3 mt-1">
              <span class="text-xs font-bold text-[#2f6fab]">Social Media Performance</span>
            </div>
            <div class="space-y-2">
              <label class="field-label text-[10px]">Insight</label>
              <textarea
                v-model="form.sections.socialMediaPerformance.insight"
                class="field-textarea"
                rows="3"
              />
            </div>
            <div class="space-y-2">
              <label class="field-label text-[10px]">Recommendation</label>
              <textarea
                v-model="form.sections.socialMediaPerformance.recommendation"
                class="field-textarea"
                rows="3"
              />
            </div>
          </template>

          <!-- AI Chatbot Management -->
          <template v-if="sectionEnabled('aiChatbotManagement')">
            <div class="space-y-2 md:col-span-2 border-t border-[#f0f4fa] pt-3 mt-1">
              <span class="text-xs font-bold text-[#2f6fab]">AI Chatbot Management</span>
            </div>
            <div class="space-y-2">
              <label class="field-label text-[10px]">Insight</label>
              <textarea
                v-model="form.sections.aiChatbotManagement.insight"
                class="field-textarea"
                rows="3"
              />
            </div>
            <div class="space-y-2">
              <label class="field-label text-[10px]">Recommendation</label>
              <textarea
                v-model="form.sections.aiChatbotManagement.recommendation"
                class="field-textarea"
                rows="3"
              />
            </div>
          </template>

          <!-- AI Podcast Progress -->
          <template v-if="sectionEnabled('aiPodcastProgress')">
            <div class="space-y-2 md:col-span-2 border-t border-[#f0f4fa] pt-3 mt-1">
              <span class="text-xs font-bold text-[#2f6fab]">AI Podcast Progress</span>
            </div>
            <div class="space-y-2">
              <label class="field-label text-[10px]">Insight</label>
              <textarea
                v-model="form.sections.aiPodcastProgress.insight"
                class="field-textarea"
                rows="3"
              />
            </div>
            <div class="space-y-2">
              <label class="field-label text-[10px]">Recommendation</label>
              <textarea
                v-model="form.sections.aiPodcastProgress.recommendation"
                class="field-textarea"
                rows="3"
              />
            </div>
          </template>

          <!-- Promotional Materials -->
          <template v-if="sectionEnabled('promotionalMaterials')">
            <div class="space-y-2 md:col-span-2 border-t border-[#f0f4fa] pt-3 mt-1">
              <span class="text-xs font-bold text-[#2f6fab]">Promotional Materials</span>
            </div>
            <div class="space-y-2">
              <label class="field-label text-[10px]">Insight</label>
              <textarea
                v-model="form.sections.promotionalMaterials.insight"
                class="field-textarea"
                rows="3"
              />
            </div>
            <div class="space-y-2">
              <label class="field-label text-[10px]">Recommendation</label>
              <textarea
                v-model="form.sections.promotionalMaterials.recommendation"
                class="field-textarea"
                rows="3"
              />
            </div>
          </template>

          <!-- Blog Management -->
          <template v-if="sectionEnabled('blogManagement')">
            <div class="space-y-2 md:col-span-2 border-t border-[#f0f4fa] pt-3 mt-1">
              <span class="text-xs font-bold text-[#2f6fab]">Blog Management</span>
            </div>
            <div class="space-y-2">
              <label class="field-label text-[10px]">Insight</label>
              <textarea
                v-model="form.sections.blogManagement.insight"
                class="field-textarea"
                rows="3"
              />
            </div>
            <div class="space-y-2">
              <label class="field-label text-[10px]">Recommendation</label>
              <textarea
                v-model="form.sections.blogManagement.recommendation"
                class="field-textarea"
                rows="3"
              />
            </div>
          </template>

          <!-- SEO Management -->
          <template v-if="sectionEnabled('seoManagement')">
            <div class="space-y-2 md:col-span-2 border-t border-[#f0f4fa] pt-3 mt-1">
              <span class="text-xs font-bold text-[#2f6fab]">SEO Management</span>
            </div>
            <div class="space-y-2 md:col-span-2">
              <label class="field-label text-[10px]">Progress Summary</label>
              <textarea
                v-model="form.sections.seoManagement.summary"
                class="field-textarea"
                rows="2"
              />
            </div>
            <div class="space-y-2 md:col-span-2">
              <label class="field-label text-[10px]">Insight</label>
              <textarea
                v-model="form.sections.seoManagement.insight"
                class="field-textarea"
                rows="3"
              />
            </div>
            <div class="space-y-2 md:col-span-2">
              <label class="field-label text-[10px]">Recommendation</label>
              <textarea
                v-model="form.sections.seoManagement.recommendation"
                class="field-textarea"
                rows="3"
              />
            </div>
          </template>

          <!-- Ads Management -->
          <template v-if="sectionEnabled('adsManagement')">
            <div class="space-y-2 md:col-span-2 border-t border-[#f0f4fa] pt-3 mt-1">
              <span class="text-xs font-bold text-[#2f6fab]">Ads Management</span>
            </div>
            <div class="space-y-2 md:col-span-2">
              <label class="field-label text-[10px]">Insight</label>
              <textarea
                v-model="form.sections.adsManagement.insight"
                class="field-textarea"
                rows="3"
              />
            </div>
          </template>

          <!-- The Three-Month Journey (Month 3 Only) -->
          <template v-if="sectionEnabled('journey')">
            <div class="space-y-2 md:col-span-2 border-t border-[#f0f4fa] pt-3 mt-1">
              <span class="text-xs font-bold text-[#2f6fab]">The Three-Month Journey</span>
            </div>
            <div class="space-y-2">
              <label class="field-label text-[10px]">Key Insight</label>
              <textarea
                v-model="form.sections.journey.insight"
                class="field-textarea"
                rows="3"
              />
            </div>
            <div class="space-y-2">
              <label class="field-label text-[10px]">Next Action / Recommendation</label>
              <textarea
                v-model="form.sections.journey.recommendation"
                class="field-textarea"
                rows="3"
              />
            </div>
          </template>

          <!-- Generated Leads -->
          <template v-if="sectionEnabled('generatedLeads')">
            <div class="space-y-2 md:col-span-2 border-t border-[#f0f4fa] pt-3 mt-1">
              <span class="text-xs font-bold text-[#2f6fab]">Generated Leads</span>
            </div>
            <div class="space-y-2">
              <label class="field-label text-[10px]">Key Insight</label>
              <textarea
                v-model="form.sections.generatedLeads.insight"
                class="field-textarea"
                rows="3"
              />
            </div>
            <div class="space-y-2">
              <label class="field-label text-[10px]">Key Learning</label>
              <textarea
                v-model="form.sections.generatedLeads.learning"
                class="field-textarea"
                rows="3"
              />
            </div>
            <div class="space-y-2">
              <label class="field-label text-[10px]">Business Meaning</label>
              <textarea
                v-model="form.sections.generatedLeads.business_meaning"
                class="field-textarea"
                rows="3"
              />
            </div>
            <div class="space-y-2">
              <label class="field-label text-[10px]">Next Action</label>
              <textarea
                v-model="form.sections.generatedLeads.recommendation"
                class="field-textarea"
                rows="3"
              />
            </div>
          </template>

          <!-- Overall Recommendation Summary -->
          <div class="space-y-2 md:col-span-2 border-t border-[#f0f4fa] pt-3 mt-2">
            <label class="field-label">Overall recommendation summary</label>
            <textarea
              v-model="form.overall_recommendation_summary"
              class="field-textarea"
              rows="5"
            />
          </div>
        </div>

        <div
          v-if="error"
          class="rounded-[1rem] border border-[#f5c2c7] bg-[#fff5f5] px-4 py-3 text-sm text-[#b42318]"
        >
          {{ error }}
        </div>

        <DialogFooter class="border-t border-[#e3eaf4] px-0 pt-4">
          <Button
            type="button"
            variant="outline"
            class="rounded-[0.95rem] border-[#d7e2ef] bg-[#f6f9ff] px-4 py-2 font-bold text-[#2855a6]"
            @click="emit('update:open', false)"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            class="rounded-[0.95rem] bg-[#173b5f] px-4 py-2 font-bold text-white"
            :disabled="loading"
          >
            {{ loading ? "Saving..." : "Save Changes" }}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, reactive, watch } from "vue";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { ReportRecord } from "@/types/reports";
import { reportSectionConfig } from "./reportSectionConfig";

const props = defineProps<{
  open: boolean;
  report: ReportRecord | null;
  loading?: boolean;
  error?: string | null;
}>();

const emit = defineEmits<{
  (event: "update:open", value: boolean): void;
  (
    event: "save",
    value: {
      title: string;
      prepared_by: string;
      executive_snapshot_note: string;
      overall_recommendation_summary: string;
      sections: Record<string, {
        enabled: boolean;
        summary?: string;
        insight: string;
        learning?: string;
        business_meaning?: string;
        recommendation?: string;
      }>;
    }
  ): void;
}>();

const isOpen = computed({
  get: () => props.open,
  set: (value: boolean) => emit("update:open", value),
});

const form = reactive({
  title: "",
  prepared_by: "",
  executive_snapshot_note: "",
  overall_recommendation_summary: "",
  sections: {
    socialMediaPerformance: { enabled: false, insight: "", recommendation: "" },
    aiChatbotManagement: { enabled: false, insight: "", recommendation: "" },
    aiPodcastProgress: { enabled: false, insight: "", recommendation: "" },
    blogManagement: { enabled: false, insight: "", recommendation: "" },
    seoManagement: { enabled: false, summary: "", insight: "", recommendation: "" },
    adsManagement: { enabled: false, insight: "" },
    generatedLeads: { enabled: false, insight: "", learning: "", business_meaning: "", recommendation: "" },
    promotionalMaterials: { enabled: false, insight: "", recommendation: "" },
    journey: { enabled: false, insight: "", recommendation: "" },
  } as Record<string, { enabled: boolean; summary?: string; insight: string; learning?: string; business_meaning?: string; recommendation?: string }>,
});

function sectionEnabled(key: string): boolean {
  const legacyKeyMap: Record<string, string> = {
    socialMediaPerformance: "social_media",
    aiChatbotManagement: "ai_chatbot",
    aiPodcastProgress: "ai_podcast",
    seoManagement: "seo_ads",
    adsManagement: "ads_management",
    generatedLeads: "generated_leads",
    promotionalMaterials: "promotional_materials",
    journey: "three_month_journey",
  };
  const legacyKey = legacyKeyMap[key];
  return !!(
    props.report?.selected_sections?.includes(key) ||
    (legacyKey && props.report?.selected_sections?.includes(legacyKey))
  );
}

function resetForm() {
  if (!props.report) return;

  const reportData = (props.report.report_data || {}) as Record<string, any>;
  const normalizedSections = (reportData.normalized_report_data?.sections || {}) as Record<string, any>;

  form.title = props.report.title || "";
  form.prepared_by = String(reportData.prepared_by || "Proweaver");
  form.executive_snapshot_note = String(reportData.executive_snapshot_note || reportData.executive_note || "");
  form.overall_recommendation_summary = String(
    reportData.overall_recommendation_summary || reportData.recommendation_summary || ""
  );

  reportSectionConfig.forEach((sec) => {
    const isEnabled = sectionEnabled(sec.key);
    const secData = reportData.sections?.[sec.key] || {};
    const normSec = normalizedSections[sec.key] || {};
    
    let summary = secData.summary || normSec.summary || reportData[`${sec.key}_summary`] || "";
    let insight = secData.insight || normSec.insight || "";
    let learning = secData.learning || normSec.learning || "";
    let businessMeaning = secData.business_meaning || normSec.business_meaning || "";
    let recommendation = secData.recommendation || normSec.recommendation || "";

    if (!reportData.sections && !reportData.normalized_report_data) {
      if (sec.key === "socialMediaPerformance") {
        insight = reportData.social_media_insight || "";
        recommendation = reportData.social_media_recommendation || "";
      } else if (sec.key === "aiChatbotManagement") {
        insight = reportData.ai_chatbot_insight || reportData.chatbot_insight || "";
        recommendation = reportData.ai_chatbot_recommendation || reportData.chatbot_recommendation || "";
      } else if (sec.key === "aiPodcastProgress") {
        insight = reportData.ai_podcast_insight || reportData.podcast_insight || "";
        recommendation = reportData.ai_podcast_recommendation || reportData.podcast_recommendation || "";
      } else if (sec.key === "seoManagement") {
        insight = reportData.seo_ads_insight || reportData.seo_insight || "";
      } else if (sec.key === "promotionalMaterials") {
        insight = reportData.promotional_materials_insight || "";
        recommendation = reportData.promotional_materials_recommendation || "";
      } else if (sec.key === "blogManagement") {
        insight = reportData.blog_insight || "";
        recommendation = reportData.blog_recommendation || "";
      } else if (sec.key === "generatedLeads") {
        insight = reportData.generated_leads_insight || "";
        learning = reportData.generated_leads_learning || "";
        businessMeaning = reportData.generated_leads_business_meaning || "";
        recommendation = reportData.generated_leads_recommendation || reportData.generated_leads_next_action || "";
      } else if (sec.key === "journey") {
        insight = reportData.journey_insight || "";
        recommendation = reportData.journey_recommendation || reportData.journey_next_action || "";
      }
    }

    form.sections[sec.key] = {
      enabled: isEnabled,
      summary: sec.key === "seoManagement" ? summary : undefined,
      insight,
      learning: sec.key === "generatedLeads" ? learning : undefined,
      business_meaning: sec.key === "generatedLeads" ? businessMeaning : undefined,
      recommendation,
    };
  });
}

function submit() {
  emit("save", {
    title: form.title,
    prepared_by: form.prepared_by,
    executive_snapshot_note: form.executive_snapshot_note,
    overall_recommendation_summary: form.overall_recommendation_summary,
    sections: { ...form.sections },
  });
}

watch(
  () => [props.open, props.report],
  ([open]) => {
    if (open) {
      resetForm();
    }
  },
  { immediate: true, deep: true }
);
</script>

<style scoped>
.field-label {
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #748197;
}

.field-input,
.field-textarea {
  width: 100%;
  border-radius: 0.95rem;
  border: 1px solid #d3dce9;
  background: white;
  padding: 0.75rem 0.9rem;
  font-size: 0.94rem;
  color: #243049;
  outline: none;
}

.field-input:focus,
.field-textarea:focus {
  border-color: #8eb4ff;
  box-shadow: 0 0 0 2px #cfe0ff;
}

.field-textarea {
  resize: vertical;
  min-height: 110px;
}
</style>
